import { getRendererConfig } from '@src/config/rendererConfig';
import { ClipPreviewEditorStore } from '@src/editor/ClipPreviewEditorStore';
import { KARAOKE_TRACK_NUMBER } from '@src/editor/constants';
import { mergeWindowingConfig } from '@src/editor/lib/TimelineWindowFilter';
import { VideoElement } from '@src/editor/models/elements/VideoElement';
import { isValidStrategy } from '@src/hooks/useLayoutStrategyConfig';
import { useAssetPrewarmingService } from '@src/services/ServiceContainerContext';
import { WarmedRenderer } from '@src/services/WarmedRenderer';
import { ClipPreviewStore } from '@src/stores-v2/ClipPreviewStore';
import {
  useAnalytics,
  useTranscription,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { VideoSourceStore } from '@src/stores-v2/VideoSourceStore';
import { wireTranslationPhraseResolver } from '@src/stores-v2/wireTranslationPhraseResolver';
import { isOriginalLang } from '@src/utility/captionLanguage';
import {
  getGraphicFontSubstitute,
  makeBrandFontVariantLookup,
} from '@src/utility/graphicFontSubstitution';
import { LogManager } from '@src/utility/Logger';
import { extractAllAssetRefs } from '@src/utility/timelineAssetUrls';
import {
  DEFAULT_SCENE_MANIPULATION_LIMIT,
  useFlagsCombination,
} from '@src/utility/useFlagsCombination';
import { projectCompositionTranslations } from '@src/videoTranscriptionProcessor/translation/projectCompositionTranslations';
import { readActiveCaptionLanguage } from '@src/videoTranscriptionProcessor/translation/readActiveCaptionLanguage';
import { reaction } from 'mobx';
import { useEffect, useRef, useState } from 'react';

import { useOutsideAlerter } from '../../transcript/useClickOutside';
import { INTERACTIVE_IFRAME_FIT } from '../interactiveCanvasFit';
import { usePrewarmedRenderer } from '../PrewarmedRendererContext';
import { ClipVideoCreatorAdapter } from './ClipVideoCreatorAdapter';

import type { LayoutStrategy } from '@src/editor/models/layout';
import type { RendererState } from '@src/renderer/RendererState';
import type { VideoSource } from '@src/stores-v2';
import type {
  ClipEditSessionStore,
  VideoClipPreview,
} from '@src/stores-v2/ClipEditSessionStore';
import type { Story } from '@src/types.ts/story';

const logger = LogManager.getLogger('hook:useClipPreviewRenderer');

type UseClipPreviewRendererParams = {
  story?: Story;
  mode?: 'player' | 'interactive';
  autoPlay?: boolean;
  /** If true, skip renderer creation (use when parent provides shared renderer) */
  disabled?: boolean;
  /** Session store that owns all local clip variant state */
  sessionStore: ClipEditSessionStore;
  /**
   * The original `videoClipPreview` prop from the modal. Used **only** as
   * a dep-array change detector — body reads go through `sessionStore`.
   *
   * Why: when resources arrive after the modal opens, the new prop's
   * `videoSource` is set in the render where the prop arrives, but
   * `sessionStore.activeVariant.videoSource` is still stale until
   * `useClipEditSession`'s follow-up `useEffect` runs `replaceClip` in
   * commit. Tying the dep to the prop's `videoSource` makes the init
   * effect re-fire in that same render. By the time the effect body runs,
   * `useClipEditSession`'s effect has already executed (earlier in source
   * order), so re-reading `sessionStore.activeVariant` inside the body
   * gives the fresh, replaceClip'd variant.
   */
  clipPropForReadiness: VideoClipPreview;
};

/**
 * Custom hook to initialize and manage Creatomate renderer for clip previews
 * Based on useQuoteCardRenderer but adapted for video clips with full branding
 */
export const useClipPreviewRenderer = ({
  story,
  mode = 'interactive',
  autoPlay = true,
  sessionStore,
  clipPropForReadiness,
}: UseClipPreviewRendererParams) => {
  // The active variant from the session store replaces the old `clip` param.
  // All reads go through this computed property; the session store is the
  // single owner of clip variant data.
  const clip = sessionStore.activeVariant;
  const userIdentity = useUserIdentity();
  const flags = useFlagsCombination(userIdentity.currentRole);
  const {
    enableAutoFraming,
    sceneManipulationLimit,
    creatorStudioLayoutStrategy,
    lockSceneBoundElements,
  } = flags;

  const analyticsStore = useAnalytics();
  const videoCreator = useVideoCreatorStore();
  const assetPrewarmingService = useAssetPrewarmingService();
  const realTranscriptionStore = useTranscription();
  const prewarmed = usePrewarmedRenderer();
  const rendererContainerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<WarmedRenderer | null>(null);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // PLAT-11445: the docked prewarmed iframe host sits above every modal
  // overlay (fixed, z-index 1000006), so a veil rendered inside the modal
  // cannot cover it. The audio-first hook hides the host through this flag
  // while the composition is audio-first (black canvas).
  const [canvasHidden, setCanvasHidden] = useState<boolean>(false);

  // Local stores
  const videoSourceStoreRef = useRef<VideoSourceStore | null>(null);
  const editorStoreRef = useRef<ClipPreviewEditorStore | null>(null);
  const adapterRef = useRef<ClipVideoCreatorAdapter | null>(null);
  // PLAT-9966: disposer for the composition translation projection reaction.
  // Run on hook unmount so the reaction doesn't outlive the clip preview.
  const compositionProjectionDisposerRef = useRef<(() => void) | null>(null);

  // Track current playhead time for scene-aware operations (reframe/crop)
  const timeRef = useRef<number>(0);
  // Track previous renderer state for external change detection
  const prevStateRef = useRef<RendererState | undefined>(undefined);
  // Track if renderer is playing
  const isPlayingRef = useRef<boolean>(false);
  /**
   * Monotonic seek counter, so a seek that waited on a window rebuild can
   * tell it has been superseded and stand down (PLAT-11119).
   */
  const seekSeqRef = useRef<number>(0);

  // Ref to hold latest videoSource — avoids putting videoSource in the dep
  // array, so reference-only changes from syncVideoToStoryEntries auto-save
  // don't trigger a full teardown + rebuild of the renderer (PLAT-9091).
  // Assigned inside the init effect from the fresh `sessionStore.activeVariant`,
  // not from the render-time `clip` closure (which can be stale during the
  // render where the prop's videoSource arrives — see `clipPropForReadiness`).
  const videoSourceRef = useRef<VideoSource | undefined>(clip.videoSource);

  useEffect(() => {
    // Re-read the freshest variant from the session store. By the time this
    // effect runs in commit, `useClipEditSession`'s useEffect has already
    // executed (earlier in source order in `FullClipModalContent`), so
    // `replaceClip` has applied the new prop into `_variants` and
    // `activeVariant` returns the populated clip.
    const freshClip = sessionStore.activeVariant;
    videoSourceRef.current = freshClip.videoSource;

    // Skip initialization if disabled (using shared renderer from parent)
    if (!rendererContainerRef.current) {
      logger.warn('Renderer container ref not set — skipping renderer init');
      return;
    }

    if (rendererRef?.current) {
      logger.warn('Renderer already initialized — skipping renderer init');
      return;
    }

    if (!videoSourceRef.current) {
      // Guard: don't set isLoading if there's no videoSource, otherwise the
      // preview gets permanently stuck in a loading state (e.g. when an
      // AssociatedVideo without videoSource is passed after a dimension switch).
      logger.warn('No videoSource available — skipping renderer init', {
        clipId: freshClip.id,
      });
      return;
    }
    setIsLoading(true);
    logger.info('Initializing clip preview renderer', {
      clipId: freshClip.id,
      mode,
      lastActionDate: freshClip.lastActionJson?.date,
    });

    // The pre-warmed renderer runs in 'interactive' mode — 'player'-only
    // consumers fall through to the cold path.
    const claimed = mode === 'interactive' ? prewarmed?.claim() : null;
    const isWarmPath = !!claimed;

    let renderer: WarmedRenderer;
    if (claimed) {
      // --- WARM PATH ---
      logger.info('Using pre-warmed renderer (warm path)', {
        clipId: freshClip.id,
      });
      renderer = claimed.renderer;
    } else {
      // --- COLD PATH (fallback) ---
      logger.info('Creating new renderer (cold path)', {
        clipId: freshClip.id,
      });
      const rendererConfig = getRendererConfig(true);
      renderer = new WarmedRenderer(
        rendererContainerRef.current,
        mode,
        rendererConfig.rendererId,
        assetPrewarmingService,
      );
    }

    rendererRef.current = renderer;

    // Track play/pause state and sync to adapter
    renderer.onPlay = () => {
      isPlayingRef.current = true;
      adapterRef.current?.updatePlayState(true);
      logger.debug('Renderer started playing');
    };

    renderer.onPause = () => {
      isPlayingRef.current = false;
      adapterRef.current?.updatePlayState(false);
      logger.debug('Renderer paused');
    };

    // Sync time to adapter for transcript cursor tracking & scene resolution
    renderer.onTimeChange = (time: number) => {
      timeRef.current = time;
      adapterRef.current?.updateTime(time);
    };

    renderer.onActiveElementsChange = (elementIds: string[]) => {
      adapterRef.current?.handleActiveElementsChangeFromRenderer(elementIds);
    };

    // ---------- Shared initialization logic ----------
    // Extracted so both warm and cold paths converge on the same store/adapter
    // setup once the renderer is ready.
    const initializeStores = async (readyRenderer: WarmedRenderer) => {
      // Guard: only initialize once (onReady can fire again on tab switch)
      if (videoSourceStoreRef.current) {
        logger.debug('Stores already initialized — skipping re-initialization');
        return;
      }

      try {
        logger.info('Renderer ready, initializing stores');

        // Configure renderer defaults (warm path does this in the warmer).
        if (!isWarmPath) {
          await readyRenderer.setZoom('auto');
          await readyRenderer.setLoop(false);
          await readyRenderer.setCacheBypassRules([]);
          await readyRenderer.setControls(false);
        }

        // Create local stores
        videoSourceStoreRef.current = new VideoSourceStore(analyticsStore);
        videoSourceStoreRef.current.connectRenderer(readyRenderer);

        editorStoreRef.current = new ClipPreviewEditorStore(
          videoSourceStoreRef.current,
        );
        // Wire the captions translation phrase resolver onto this clip-scoped
        // editor at construction (same shared story-level translationStore).
        // Without it, ProduceKaraokeCommand falls through to the
        // original-language path and rendered captions never switch language.
        wireTranslationPhraseResolver(
          editorStoreRef.current.editor,
          videoCreator.rootStore.translationStore,
        );
        // Required by the new renderer id from getRendererConfig(true).
        editorStoreRef.current.editor.setFlag(
          'reorderLayoutCompositionTrackToBottom',
          true,
        );

        // Apply static editor-store configuration from feature flags here
        // — NOT in the adapter constructor — so all readers of
        // `isWindowingActive` or the preview-layout config see the final
        // state. Includes the bulk-warm gate just below and
        // `refreshTimeline` inside `loadProjectForPreview`.
        const validatedStrategy: LayoutStrategy = isValidStrategy(
          creatorStudioLayoutStrategy,
        )
          ? creatorStudioLayoutStrategy
          : 'hybrid';
        editorStoreRef.current.setPreviewLayoutConfig({
          strategy: validatedStrategy,
          sceneCountThreshold:
            sceneManipulationLimit ?? DEFAULT_SCENE_MANIPULATION_LIMIT,
          lockSceneBoundElements: lockSceneBoundElements ?? true,
        });
        editorStoreRef.current.setWindowingConfig(
          mergeWindowingConfig(flags.creatorStudioTimelineWindowingConfig),
        );

        // Get video source and optimize for low-res preview
        let videoSource: VideoSource | undefined = videoSourceRef.current;
        if (!videoSource) {
          logger.error('No video source found in clip');
          setIsLoading(false);
          return;
        }

        // Pre-cache the main (low-res) video source first so it wins priority
        // over the bulk warm. AssetPrewarmingService dedupes against the
        // warmer's in-flight fetch (no duplicate request).
        const lowResUrl = ClipPreviewStore.getLowResVideoUrl(story);
        const videoUrl =
          lowResUrl || ClipPreviewStore.extractVideoUrl(videoSourceRef.current);
        if (videoUrl) {
          readyRenderer.warmAsset(videoUrl).catch((err: unknown) => {
            logger.warn(
              '[useClipPreviewRenderer] Failed to cache main video source',
              { error: err, videoUrl },
            );
          });
        }

        if (editorStoreRef.current?.isWindowingActive) {
          const refs = extractAllAssetRefs(videoSourceRef.current);
          if (refs.length) {
            readyRenderer.warmAssets(refs, 0).catch((err: unknown) => {
              logger.warn('[useClipPreviewRenderer] Bulk asset warm failed', {
                error: err,
              });
            });
          }
        }

        const transcript = freshClip.transcriptionSnapshot?.elements || [];
        const karaokeData = freshClip.karaokeData;
        const scenes = freshClip.scenes;

        // Use the full video's original transcript as the baseline for redline diff.
        // This enables the Transcript component to show the full diff (what was
        // cut from the original video to create this clip).
        const originalTranscription =
          realTranscriptionStore.getOriginalTranscription()?.elements ||
          undefined;

        // Create adapter for clip operations (including publishing)
        logger.info('Creating ClipVideoCreatorAdapter');
        adapterRef.current = new ClipVideoCreatorAdapter({
          editorStore: editorStoreRef.current,
          videoSourceStore: videoSourceStoreRef.current,
          renderer: readyRenderer,
          story,
          organization: videoCreator.organization,
          realVideoCreator: videoCreator,
          sessionStore,
          flags: {
            enableAutoFraming,
            sceneManipulationLimit,
          },
        });

        // PLAT-9966: install the composition translation projection on the
        // clip-local VideoSourceStore. The projection has two triggers:
        //   1. Caption language change — fired imperatively from
        //      `ClipTranscriptBridge` via `refreshSource()` after
        //      `applyCaptionsLanguage` lands. Necessary because karaoke
        //      config is a plain class, not MobX-observable, so a reaction
        //      on it wouldn't fire.
        //   2. Translation data landing for the already-active language —
        //      fired by the MobX reaction below, which subscribes to the
        //      observable `translations.get(activeLang)` slot.
        // Together these cover the two paths that can change projection
        // output without `_source` itself changing.
        const clipVideoSourceStore = videoSourceStoreRef.current;
        const clipEditorStore = editorStoreRef.current;
        const clipAdapter = adapterRef.current;
        const translationStore = videoCreator.rootStore.translationStore;
        clipVideoSourceStore.setOutboundTransform(source => {
          const activeLang = readActiveCaptionLanguage(clipEditorStore.editor);
          if (isOriginalLang(activeLang)) return { outbound: source };
          const elements = (source as { elements?: unknown }).elements;
          if (!Array.isArray(elements) || elements.length === 0)
            return { outbound: source };
          const extraElementData =
            (clipAdapter.currentVideo?.extraElementData as
              | Record<string, unknown>
              | undefined) ?? null;
          const phrasesByNormalizedSource =
            translationStore.getCompositionPhrasesByNormalizedSource(
              activeLang,
            );
          // PLAT-11466 — same font correction the canvas applies, so the clip
          // preview cannot disagree with what publish will render.
          const brandVariants = makeBrandFontVariantLookup(
            videoCreator.organization?.branding?.customFonts,
          );
          const projection = projectCompositionTranslations({
            videoSourceElements: elements as ReadonlyArray<
              Record<string, unknown>
            >,
            extraElementData,
            activeLanguage: activeLang,
            compositionPhrasesByNormalizedSource: phrasesByNormalizedSource,
            resolveFontSubstitute: (family, lang) =>
              getGraphicFontSubstitute(family, lang, brandVariants),
          });
          if (!projection.changed) return { outbound: source };
          return {
            outbound: {
              ...(source as Record<string, unknown>),
              elements: projection.elements,
            } as unknown as VideoSource,
            rewrites: projection.rewrites,
          };
        });

        // Trigger 2: translation lands for the already-active language.
        const compositionProjectionDisposer = reaction(
          () => {
            const lang = readActiveCaptionLanguage(clipEditorStore.editor);
            if (isOriginalLang(lang)) return null;
            return translationStore.translations.get(lang) ?? null;
          },
          () => {
            clipVideoSourceStore.refreshSource();
          },
          { name: 'clip-composition-translation-projection-refresh' },
        );
        compositionProjectionDisposerRef.current =
          compositionProjectionDisposer;

        // Fire branding fetch early so it runs in parallel with sync work below
        const brandingPromise = adapterRef.current.refetchBrandingTemplates(
          freshClip.aspectRatio,
        );

        const preparedVideoSource =
          adapterRef.current.prepareVideoSourceForEditor(
            videoSource,
            freshClip.aspectRatio,
          );

        // Determine the original video track ID using the same logic as
        // VideoCreatorStore.renderVideo — matches source URLs to the story's
        // known video URLs so b-roll tracks are not mistaken for the original.
        const originalVideoTrackId =
          videoCreator.pickOriginalVideoTrack(
            preparedVideoSource as Record<string, any>,
          ) ?? null;
        // Always use loadProjectForPreview for complete initialization
        editorStoreRef.current.loadProjectForPreview({
          videoSource: preparedVideoSource,
          transcript: transcript,
          originalTranscription,
          karaokeData,
          // PLAT-10788 — per-language karaoke + language context (DS8/DS9).
          karaokeDataByLanguage: freshClip.karaokeDataByLanguage,
          transcriptLanguage: realTranscriptionStore.rawLanguage,
          originalVideoLanguage: story?.originalVideoLanguage,
          scenes,
          originalVideoTrackId,
          extraElementData: freshClip.extraElementData,
        });

        if (freshClip?.activeVideoTemplateId) {
          adapterRef.current.pendingTemplateReapplyId =
            freshClip.activeVideoTemplateId;

          sessionStore.updateVariant(freshClip.aspectRatio, {
            activeVideoTemplateId: undefined,
          });
        }

        // Await both the branding fetch and setTime together
        await Promise.all([brandingPromise, readyRenderer.setTime(0)]);

        setIsReady(true);

        logger.info('Clip preview renderer ready');
      } catch (error) {
        logger.error('Error initializing renderer', error);
      }
    };

    // --- Warm path: set up position tracking + init stores immediately ---
    let resizeObserver: ResizeObserver | undefined;
    let syncPosition: (() => void) | undefined;

    if (isWarmPath) {
      // Position the pre-warmed iframe over the RendererContainer target.
      const target = rendererContainerRef.current;
      syncPosition = () => {
        const rect = target.getBoundingClientRect();
        if (!prewarmed) {
          logger.warn(
            'Prewarmed renderer instance missing during position sync',
          );
        } else {
          prewarmed.positionOver(
            rect,
            { borderRadius: '10px 10px 0 0' },
            INTERACTIVE_IFRAME_FIT,
          );
        }
      };
      syncPosition(); // initial position

      resizeObserver = new ResizeObserver(syncPosition);
      resizeObserver.observe(target);
      window.addEventListener('resize', syncPosition);

      // Renderer is already ready — initialize stores immediately.
      initializeStores(renderer);
    } else {
      // --- Cold path: wait for onReady ---
      renderer.onReady = async () => {
        await initializeStores(renderer);
      };
    }

    renderer.onLoadComplete = () => {
      // Buffer: Creatomate fires onLoadComplete the instant the source is
      // loaded, but the iframe still needs ~one frame to paint the new
      // content. Without this delay, the iframe (and its overlay spinner)
      // un-hide while the iframe is briefly showing a thumbnail / black
      // frame from the previous source.
      setTimeout(() => setIsLoading(false), 300);
      if (autoPlay) {
        setTimeout(async () => {
          await renderer.play();
        }, 100);
      }
    };

    // Cleanup on unmount
    return () => {
      logger.info('Cleaning up clip preview renderer');
      setIsReady(false);

      // Clean up position tracking (warm path only)
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (syncPosition) {
        window.removeEventListener('resize', syncPosition);
      }

      if (compositionProjectionDisposerRef.current) {
        compositionProjectionDisposerRef.current();
        compositionProjectionDisposerRef.current = null;
      }

      if (adapterRef.current) {
        adapterRef.current.dispose();
        adapterRef.current = null;
      }

      if (videoSourceStoreRef.current) {
        videoSourceStoreRef.current.disconnectRenderer();
        videoSourceStoreRef.current = null;
      }

      if (editorStoreRef.current) {
        editorStoreRef.current.dispose();
        editorStoreRef.current = null;
      }
      if (isWarmPath) {
        // Clear all callbacks we attached to the shared renderer so stale
        // closures from this hook instance can't fire while the renderer
        // sits offscreen between claims.  The next claim() will re-attach
        // fresh handlers.
        renderer.onPlay = undefined;
        renderer.onPause = undefined;
        renderer.onTimeChange = undefined;
        renderer.onLoadComplete = undefined;
        renderer.onStateChange = undefined;
        renderer.onActiveElementsChange = undefined;

        prewarmed?.unclaim();
        rendererRef.current = null;
      } else {
        // Cold path: dispose the renderer we created.
        // WarmedRenderer.dispose() override evicts our per-renderer cache
        // entry automatically — no explicit forgetRenderer needed.
        if (rendererRef.current) {
          rendererRef.current.dispose();
          rendererRef.current = null;
        }
      }

      // Clear state tracking refs
      prevStateRef.current = undefined;
      isPlayingRef.current = false;
    };
    // Deps: tear down + rebuild only when mode changes or when videoSource
    // first becomes available (undefined -> defined).
    // Reference-only changes to videoSource (caused by syncVideoToStoryEntries
    // during auto-save) are not a reason to rebuild — see PLAT-9091.
    // The actual videoSource value is read from videoSourceRef.
    //
    // Aspect ratio changes are handled in-place via setSource() on the
    // existing renderer — no teardown needed. Existing-variant switching
    // goes through the adapter's switchToExistingVariant method, which
    // calls loadProjectForPreview on the same editor instance.
    //
    // We watch `clipPropForReadiness.videoSource` (the modal's prop) rather
    // than `sessionStore.activeVariant.videoSource` so the effect re-fires
    // in the same render where the prop's `videoSource` arrives. If we
    // watched the session-store getter, the dep would still be stale at
    // that render because `replaceClip` runs in a follow-up `useEffect`
    // (see `useClipEditSession`).
    // `assetPrewarmingService` is included because the cold-path
    // `new WarmedRenderer(..., assetPrewarmingService)` constructor reads
    // it. In practice the container is a tree-lifetime singleton, so this
    // dep doesn't cause real re-mounts.
  }, [mode, !!clipPropForReadiness.videoSource, assetPrewarmingService]);

  // Warm path only: hide the docked prewarmed iframe while a source swap
  // (aspect-ratio toggle, switch to existing variant) is in flight. Prevents
  // stale renderer content from peeking through inside the resized canvas
  // before the new source has fully loaded. Cold-path iframe lives inside
  // RendererContainer and is already hidden via that element's opacity.
  // Asymmetric transition: snap to hidden so the user never sees stale
  // content during the fade-out, but fade back in smoothly to mask any
  // residual paint flicker as the iframe settles on the new source.
  useEffect(() => {
    if (!prewarmed || !isReady) return;
    const hidden = isLoading || canvasHidden;
    prewarmed.setHostStyle({
      opacity: hidden ? '0' : '1',
      transition: hidden ? 'opacity 0.05s ease-out' : 'opacity 0.25s ease-in',
    });
  }, [prewarmed, isReady, isLoading, canvasHidden]);

  // Load transcript into editor when it becomes available after async resource fetch.
  // The main useEffect may initialize the editor before resources are populated,
  // so transcript data can arrive late (after renderer is already ready).
  const transcriptElements = (clip as any).transcriptionSnapshot?.elements;
  const karaokeData = clip.karaokeData;
  const karaokeDataByLanguage = clip.karaokeDataByLanguage;

  useEffect(() => {
    const editor = editorStoreRef.current;

    if (!isReady || !editor || isLoading) return;
    if (!transcriptElements?.length) return;

    const editorKaraokeTrack = editor.editor.getKaraokeTrack();
    const videoSource = videoSourceStoreRef.current?.source;
    const hasKaraokeTrackInSource = !!videoSource?.elements?.find(
      (el: any) => el.track === KARAOKE_TRACK_NUMBER,
    );

    // Skip if editor already has transcript loaded
    if (
      editor.transcriptElements?.length &&
      (editorKaraokeTrack?.elements?.length || !hasKaraokeTrackInSource)
    ) {
      return;
    }

    // Use the full video's original transcript as baseline for redline diff,
    // falling back to the clip's own transcript if not available.
    const fullOriginal =
      realTranscriptionStore.getOriginalTranscription()?.elements ||
      transcriptElements;
    // Ensure video elements have media_duration before loading transcript.
    // TranscriptTrack.fromVideoTrackAndTranscript requires it for tail clips.
    const mediaDuration = story?.originalVideo?.video?.duration;
    if (typeof mediaDuration === 'number' && mediaDuration > 0) {
      for (const track of editor.editor.timeline.tracks) {
        for (const el of track.elements) {
          if (el instanceof VideoElement && el.media_duration == null) {
            el.media_duration = mediaDuration;
          }
        }
      }
    }

    logger.info('Late-loading transcript into editor', {
      elementCount: transcriptElements.length,
      fullOriginalCount: fullOriginal.length,
    });
    editor.editor.loadTranscript(transcriptElements, fullOriginal, undefined);
    // Update observable field so MobX observers (Transcript component) re-render
    editor.refreshTranscriptElements();

    // Reload karaoke data now that transcript track exists.
    // loadKaraokeData requires the transcript track (set by loadTranscript above).
    // During initial loadProjectForPreview, karaoke loading silently fails if
    // transcript wasn't available yet — this re-attempts it.
    if ((karaokeData || karaokeDataByLanguage) && !editor.karaokeLines.length) {
      if (hasKaraokeTrackInSource) {
        logger.info('Late-loading karaoke data into editor');
        // PLAT-10788 — route through the per-language hydrate (DS8/DS9) so the
        // clip's KaraokeStore active language + cache stay correct.
        editor.hydrateAndLoadKaraoke({
          karaokeData,
          karaokeDataByLanguage,
          transcriptLanguage: realTranscriptionStore.rawLanguage,
          originalVideoLanguage: story?.originalVideoLanguage,
        });
        editor.refreshKaraokeState();
      }
    }
  }, [
    isReady,
    transcriptElements,
    isLoading,
    karaokeData,
    karaokeDataByLanguage,
  ]);

  // Clip-preview-only: click outside the canvas deselects.
  // Skip the hook's synthetic window-blur event (isTrusted=false) — it fires
  // whenever focus moves to an iframe, but the warm Creatomate iframe lives
  // outside `rendererContainerRef`'s DOM tree and would otherwise trigger a
  // deselect on every first canvas click.
  useOutsideAlerter(rendererContainerRef, event => {
    if (!event.isTrusted) return;
    const adapter = adapterRef.current;
    if (adapter && adapter.activeElementIds.length > 0) {
      adapter.setActiveElements();
    }
  });

  // Playback controls
  //
  // PLAT-11119 — these drive the preview's transport (the Play button, the
  // scrubber, the seek buttons) and talk to the renderer directly rather than
  // through the adapter, so they need the same window guard the adapter has.
  // The renderer only holds the windowed slice of the timeline; commanding it
  // to a position outside that slice asks it to present footage it was never
  // sent, and it shows black until a background refresh catches up.
  const play = async () => {
    if (!rendererRef.current?.ready) return;
    // Playback runs forward from the playhead, so the window has to reach it
    // before the renderer starts rather than 500ms into the run.
    await editorStoreRef.current?.ensureWindowCovers(timeRef.current);
    rendererRef.current?.play();
  };

  const pause = () => {
    if (rendererRef.current?.ready) {
      rendererRef.current.pause();
    }
  };

  const setTime = async (time: number) => {
    if (!rendererRef.current?.ready) return;

    // Skipped while scrubbing, matching `ClipVideoCreatorAdapter.setTime`: a
    // drag issues seeks continuously, and the scrubbing window has its own
    // debounced reaction in `AdaptivePlaybackSourceManager`.
    if (!adapterRef.current?.isScrubbing) {
      // Awaiting here means a later seek can overtake an earlier one whose
      // window is still rebuilding, and the loser would land last and win.
      // Seeks were issued synchronously before this guard existed, so the
      // ordering has to be restored explicitly: only the newest seek may
      // command the renderer.
      const seek = ++seekSeqRef.current;
      await editorStoreRef.current?.ensureWindowCovers(time);
      if (seek !== seekSeqRef.current) return;
    }

    rendererRef.current?.setTime(time);
    if (isPlayingRef.current) {
      rendererRef.current.play();
    }
  };

  return {
    mode,
    rendererContainerRef,
    rendererRef,
    renderer: rendererRef.current,
    isReady,
    isLoading,
    editorStore: editorStoreRef.current,
    videoSourceStore: videoSourceStoreRef.current,
    adapter: adapterRef.current,
    // Playback controls
    play,
    pause,
    setTime,
    setIsLoading,
    canvasHidden,
    setCanvasHidden,
    isPreviewLoading: isLoading,
  };
};
